import { ReportingLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { INTEGRATION_TYPE, IntegrationType } from '../../TelSysConfiguration/Constants/Constants'

/**
 * Maps a base reporting-level i18n key to its MS Teams Business label key.
 *
 * <p>Business licences reuse the existing Essentials / Professional / CCA / CCS reporting types but are
 * displayed under different names (Historic User / Real-Time User / Agent / Manager). Only the four mapped
 * levels have a Business alternative; all other levels fall back to the standard label.
 */
const BUSINESS_REPORTING_LABEL_KEYS: Readonly<Record<string, string>> = {
    [ReportingLanguage.AccessLevelEssentials]: ReportingLanguage.AccessLevelHistoricUser,
    [ReportingLanguage.AccessLevelProfessional]: ReportingLanguage.AccessLevelRealTimeUser,
    [ReportingLanguage.AccessLevelCallCentreAgent]: ReportingLanguage.AccessLevelAgent,
    [ReportingLanguage.AccessLevelCallCentreSupervisor]: ReportingLanguage.AccessLevelManager
}

/**
 * Resolves the display label for a reporting access level, accounting for the telsys integration type.
 *
 * <p>For an MS Teams telsys with Business integration type the four business licences are shown under their
 * business names. For every other case (MS Teams Legacy, Webex, Broadworks, etc.) the existing
 * {@link CLanguageHandler.getWithTelSysCheck} behaviour is preserved, so this never changes Legacy/CXA or WxC labels.
 *
 * @param language - Language handler used to resolve the label.
 * @param baseKey - Base reporting-level i18n key (e.g. {@link ReportingLanguage.AccessLevelEssentials}).
 * @param telSysType - Current telephony system type.
 * @param integrationType - Telsys integration type (`'legacy'` or `'business'`), when known.
 * @returns The resolved label string for the current telsys / integration type.
 */
export function resolveReportingLevelLabel(
    language: CLanguageHandler,
    baseKey: string,
    telSysType: Nullable<TelSysTypes>,
    integrationType: Nullable<IntegrationType>
): string {
    if (telSysType === TelSysTypes.MS_TEAMS && integrationType === INTEGRATION_TYPE.BUSINESS) {
        const businessKey: Definable<string> = BUSINESS_REPORTING_LABEL_KEYS[baseKey]

        if (businessKey) return language.get(businessKey)
    }

    return language.getWithTelSysCheck(baseKey, telSysType)
}
